import React, { useState, useCallback, useRef } from 'react';
import './index.scss';

interface ITagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  label?: string;
}

const TagInput: React.FC<ITagInputProps> = ({
  tags,
  onChange,
  placeholder = 'Enter tag...',
  label,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = useCallback((value: string): void => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInputValue('');
  }, [tags, onChange]);

  const handleRemove = useCallback((index: number): void => {
    const next = [...tags];
    next.splice(index, 1);
    onChange(next);
  }, [tags, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      handleRemove(tags.length - 1);
    }
  }, [addTag, handleRemove, inputValue, tags.length]);

  return (
    <div className="dbh-tag-input">
      {label && <span className="dbh-tag-input__label">{label}</span>}
      <div
        className="dbh-tag-input__container"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, idx) => (
          <span className="dbh-tag-input__chip" key={`${tag}-${idx}`}>
            <span className="dbh-tag-input__chip-text">{tag}</span>
            <span
              className="dbh-tag-input__chip-close"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(idx);
              }}
            >
              &times;
            </span>
          </span>
        ))}
        <input
          ref={inputRef}
          className="dbh-tag-input__input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(inputValue)}
          placeholder={tags.length === 0 ? placeholder : ''}
        />
      </div>
    </div>
  );
};

export default TagInput;
